import { useForm, useFieldArray } from "react-hook-form";
import InputWithLabel from "../components/InputWithLabel";
import { XCircleIcon, PlusCircleIcon, SpinnerIcon } from "../components/Icons";
import taramLogoFull from "../assets/icons/TaramFullLogo.png";
import { assign_contacts } from "../constants/apis";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import InvalidLinkPage from "./InvalidLinkPage";

export default function AssignContacts() {
  const [searchParams] = useSearchParams();
  const hashId = searchParams.get("hash_id");
  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      contacts: [{ name: "", email: "", phone: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contacts",
  });

  const onSubmit = async (data: any) => {
    try {
      const res = await fetch(assign_contacts, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contacts_data: data.contacts, token: hashId }),
      });

      if (!res.ok) {
        // Try to parse error for better message
        let message = "Failed to assign contacts.";
        try {
          const err = await res.json();
          message = err?.message || message;
        } catch (_) {}
        throw new Error(message);
      }

      toast.success("Contacts have been assigned successfully.");
      reset({ contacts: [{ name: "", email: "", phone: "" }] });
    } catch (err) {
      toast.error("Something went wrong while assigning contacts.");
    }
  };

  if (!hashId) return <InvalidLinkPage />;

  return (
    <section className="bg-gray-100">
      <div className="w-1/3 mx-auto flex justify-center items-center p-6">
        <div className="bg-white shadow-lg rounded-lg w-full max-w-2xl p-6">
          <div className="mb-1">
            <img
              src={taramLogoFull}
              alt="logo"
              width={100}
              height={20}
              className="full_logo"
            />
          </div>

          <div className="space-y-1 mb-4">
            <h2 className="text-gray-900 text-lg font-semibold">
              Assign New Contacts
            </h2>
            <p className="text-xs text-gray-500">
              Easily add and assign new contacts to this step. Provide the name,
              email, and phone number of the contact to keep everyone connected
              and informed.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative"
              >
                <h4 className="text-lg font-semibold text-gray-700 mb-4">
                  Contact {index + 1}
                </h4>

                {/* Name */}
                <div className="mb-4">
                  <InputWithLabel
                    label="Name*"
                    type="text"
                    {...register(`contacts.${index}.name`, {
                      required: "Name is required",
                    })}
                    placeholder="Enter full name"
                    className="mt-1 block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                  {errors.contacts?.[index]?.name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.contacts[index].name.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-4">
                  <InputWithLabel
                    label="Email*"
                    type="email"
                    {...register(`contacts.${index}.email`, {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email address",
                      },
                    })}
                    placeholder="Enter email address"
                    className="mt-1 block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                  {errors.contacts?.[index]?.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.contacts[index].email.message}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div className="mb-4">
                  <InputWithLabel
                    label="Phone"
                    type="text"
                    {...register(`contacts.${index}.phone`)}
                    placeholder="Enter phone number"
                    className="mt-1 block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                </div>

                {/* Remove Contact */}
                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="absolute top-3 right-3 text-sm text-red-500 hover:text-red-700"
                  >
                    <XCircleIcon width={24} height={24} />
                  </button>
                )}
              </div>
            ))}

            {/* Add Contact */}
            <div className="flex justify-end text-custom-blue items-center gap-2">
              <button
                type="button"
                onClick={() => append({ name: "", email: "", phone: "" })}
                className="flex justify-end items-center gap-2"
              >
                <PlusCircleIcon width={24} height={24} />
                Add Contact
              </button>
            </div>
            <button
              type="submit"
              className="px-6 py-2 w-full bg-custom-blue-medium text-white rounded-md shadow disabled:bg-custom-blue-medium/60 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
              ) : (
                "Submit"
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
